import { useState } from 'react';
import { storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

interface ImageUploadProps {
  onImageUploaded: (url: string) => void;
  currentImageUrl?: string;
}

const ImageUpload = ({ onImageUploaded, currentImageUrl }: ImageUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>(currentImageUrl || '');

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview immediately after selection
    setPreviewUrl(URL.createObjectURL(file));
    setSuccess(false);
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file');
      return;
    }

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      
      img.onload = async () => {
        URL.revokeObjectURL(objectUrl);

        // Allow some flexibility in the aspect ratio (±10%)
        const targetRatio = 4/3;
        const actualRatio = img.width / img.height;
        const tolerance = 0.1;
        const isValidRatio = Math.abs(actualRatio - targetRatio) < tolerance;

        // Changed to minimum width of 800px
        if (img.width < 800) {
          setError('Image width must be at least 800px');
          setUploading(false);
          return;
        }

        // Upload to Firebase Storage
        const storageRef = ref(storage, `game-images/${Date.now()}-${file.name}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        
        onImageUploaded(url);
        setSuccess(true);
        setUploading(false);
        
        // Hide success message after 3 seconds
        setTimeout(() => setSuccess(false), 3000);
      };

      img.src = objectUrl;
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-4">
        <div>
          <label className="relative cursor-pointer">
            <span className="bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600 transition-colors">
              {uploading ? 'Uploading...' : 'Upload Image'}
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
          {success && (
            <p className="text-green-400 text-sm mt-2">
              ✓ Image uploaded successfully!
            </p>
          )}
          {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
        </div>

        {/* Image Preview */}
        {previewUrl && (
          <div className="relative">
            <img 
              src={previewUrl}
              alt="Preview"
              className="w-32 h-24 object-cover rounded"
            />
            <button
              onClick={() => setPreviewUrl('')}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
              title="Remove preview"
            >
              ×
            </button>
          </div>
        )}
      </div>

      <div className="text-sm text-gray-400">
        Image requirements:
        <ul className="list-disc list-inside ml-2">
          <li>4:3 aspect ratio</li>
          <li>Minimum width: 800px</li>
          <li>Maximum size: 2MB</li>
          <li>Supported formats: JPG, PNG</li>
        </ul>
      </div>
    </div>
  );
};

export default ImageUpload; 